import { useState } from 'react';
import { EXAMPLE_CASES, type ExampleCase } from '../../lib/exampleCards';
import { useSampleLoader } from './useSampleLoader';
import type { AppState } from '../../hooks/useAppState';

interface Props {
  app: AppState;
}

/** Always-present toolbar button: opens a modal with full-card preview
 *  tiles. Stays available even after the user has uploaded their own
 *  images, so they can swap to a sample at any time. */
export function ExampleToolbarLauncher({ app }: Props) {
  const [open, setOpen] = useState(false);
  const { loadingId, load } = useSampleLoader(app);

  const handleLoad = async (id: ExampleCase['id']) => {
    const c = EXAMPLE_CASES.find((x) => x.id === id);
    if (!c) return;
    await load(c);
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        className="ex-launcher-btn"
        onClick={() => setOpen(true)}
        aria-expanded={open}
        title="Try a finished example"
      >
        🎴 Examples
      </button>

      {open && (
        <div
          className="ex-launcher-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => setOpen(false)}
        >
          <div className="ex-launcher-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="ex-launcher-close"
              onClick={() => setOpen(false)}
              aria-label="Close"
            >
              ✕
            </button>
            <h3 className="ex-launcher-title">Try a finished example</h3>
            <p className="ex-launcher-meta">
              Loads all four images, card size, paper, and orientation in one
              click — no need to set anything up yourself. You can edit
              freely after.
            </p>
            <div className="ex-launcher-grid">
              {EXAMPLE_CASES.map((c) => (
                <article key={c.id} className="ex-launcher-card">
                  <img src={c.thumb} alt={c.name} className="ex-launcher-card-thumb" />
                  <div className="ex-launcher-card-body">
                    <h4 className="ex-launcher-card-title">{c.name}</h4>
                    <p className="ex-launcher-card-blurb">{c.blurb}</p>
                    <button
                      type="button"
                      className="ex-launcher-card-cta"
                      onClick={() => handleLoad(c.id)}
                      disabled={loadingId !== null}
                    >
                      {loadingId === c.id ? 'Loading…' : 'Load this'}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
